import { apiService, type ApiResponse } from '@/services/apiService';
import {
  clearOfflineApiCache,
  OFFLINE_CACHE_KEYS,
  readOfflineApiCache,
  writeOfflineApiCache,
} from '@/services/apiOfflineCache';
import { getProviderIconPaths } from '@/features/games/data/gameProviders';

export interface GameProviderApiResponse {
  id: string;
  code: string;
  name: string;
  currency: string;
  minDeposit: number;
  maxDeposit: number;
  status: 'active' | 'inactive' | 'maintenance';
  displayOrder: number;
  clientTag: string | null;
  config?: {
    depositType?: 'auto' | 'manual';
  };
  createdAt: string;
  updatedAt: string;
}

export interface GameProvidersResponse {
  items: GameProviderApiResponse[];
  statusSummary: {
    total: number;
    active: number;
    inactive: number;
    maintenance: number;
  };
}

// In-memory cache for API response
let apiCache: ApiResponse<GameProvidersResponse> | null = null;
let apiCacheTimestamp: number = 0;
const API_CACHE_TTL = 30 * 60 * 1000; // 30 minutes cache (server should bust via versioning when changed)
const preloadedIconPaths = new Set<string>();

/**
 * Maps raw API item (snake_case from backend) to our interface
 */
/** Kiosk must never list maintenance providers (defense in depth vs API) — same as V1. */
function filterKioskVisibleProviders(items: GameProviderApiResponse[]): GameProviderApiResponse[] {
  return items.filter((i) => i.status !== 'maintenance');
}

function mapApiItem(item: any): GameProviderApiResponse {
  const rawOrder = item.displayOrder ?? item.display_order;
  let displayOrder = 1000;
  if (rawOrder !== undefined && rawOrder !== null && rawOrder !== '') {
    const n = typeof rawOrder === 'number' ? rawOrder : parseInt(String(rawOrder), 10);
    if (Number.isFinite(n)) displayOrder = n;
  }
  const rawTag = item.clientTag ?? item.client_tag;
  let clientTag: string | null = null;
  if (rawTag != null && String(rawTag).trim() !== '') {
    clientTag = String(rawTag).trim().slice(0, 64);
  }
  return {
    id: item.id,
    code: item.code,
    name: item.name,
    currency: item.currency || 'USD',
    minDeposit: typeof item.minDeposit === 'string' ? parseFloat(item.minDeposit) : (item.minDeposit ?? 5),
    maxDeposit: typeof item.maxDeposit === 'string' ? parseFloat(item.maxDeposit) : (item.maxDeposit ?? 10000),
    status: item.status || 'inactive',
    displayOrder,
    clientTag,
    config: item.config ? { depositType: item.config.depositType || 'auto' } : { depositType: 'auto' },
    createdAt: item.created_at || item.createdAt || new Date().toISOString(),
    updatedAt: item.updated_at || item.updatedAt || new Date().toISOString()
  };
}

/**
 * Builds a GameProvidersResponse from an array of API items
 */
function buildResponse(items: GameProviderApiResponse[]): GameProvidersResponse {
  return {
    items,
    statusSummary: {
      total: items.length,
      active: items.filter(i => i.status === 'active').length,
      inactive: items.filter(i => i.status === 'inactive').length,
      maintenance: items.filter(i => i.status === 'maintenance').length
    }
  };
}

function loadGameProvidersFromOfflineStorage(): ApiResponse<GameProvidersResponse> | null {
  const cached = readOfflineApiCache<ApiResponse<GameProvidersResponse>>(OFFLINE_CACHE_KEYS.GAME_PROVIDERS);
  if (!cached?.success || !cached.data?.items) return null;
  return cached;
}

/**
 * Gets cached game providers synchronously (for instant display before API loads)
 * Returns cached API response if available, otherwise null
 */
export function getCachedProvidersSync(): ApiResponse<GameProvidersResponse> | null {
  if (apiCache?.data && (Date.now() - apiCacheTimestamp) < API_CACHE_TTL) {
    const cacheData = apiCache.data;
    const visible = filterKioskVisibleProviders(cacheData.items);
    if (visible.length === cacheData.items.length) {
      return apiCache;
    }
    return {
      ...apiCache,
      data: buildResponse(visible)
    };
  }
  const persisted = loadGameProvidersFromOfflineStorage();
  if (!persisted?.data) return null;
  const persistedData = persisted.data;
  const visible = filterKioskVisibleProviders(persistedData.items);
  if (visible.length === persistedData.items.length) {
    return persisted;
  }
  return {
    ...persisted,
    data: buildResponse(visible)
  };
}

/**
 * Clears the cached API response
 */
export function clearGameProvidersCache(): void {
  apiCache = null;
  apiCacheTimestamp = 0;
  clearOfflineApiCache(OFFLINE_CACHE_KEYS.GAME_PROVIDERS);
  if (import.meta.env.DEV) console.log('[GameProviders] Cache cleared');
}

/**
 * Gets all game providers from the server API
 * Endpoint: GET /game-providers (no auth required)
 * @param forceRefresh - If true, bypasses cache
 * @returns Promise with game providers data
 */
export async function getGameProviders(forceRefresh: boolean = false): Promise<ApiResponse<GameProvidersResponse>> {
  // Return cache if valid and not forcing refresh
  if (!forceRefresh && apiCache && (Date.now() - apiCacheTimestamp) < API_CACHE_TTL) {
    console.log('[GameProviders] Returning cached API data');
    return apiCache;
  }

  try {
    // Call the real API - no auth required
    const response = await apiService.get<any>('/game-providers', false);

    if (response.success && response.data) {
      // Handle both array and { items: [...] } response formats
      const rawItems: any[] = Array.isArray(response.data) ? response.data : (response.data.items || []);

      // Strip maintenance (V1): inactive stays visible as Coming soon on Home / picker.
      const items = filterKioskVisibleProviders(rawItems.map(mapApiItem));
      const result: ApiResponse<GameProvidersResponse> = {
        success: true,
        message: 'OK',
        statusCode: 200,
        data: buildResponse(items)
      };

      // Update cache
      apiCache = result;
      apiCacheTimestamp = Date.now();
      writeOfflineApiCache(OFFLINE_CACHE_KEYS.GAME_PROVIDERS, result);

      if (import.meta.env.DEV) console.log(`[GameProviders] Fetched ${items.length} providers from API`);
      return result;
    }

    // API returned unexpected format
    console.error('[GameProviders] API returned unexpected data:', response);
    const offlineUnexpected = loadGameProvidersFromOfflineStorage();
    if (offlineUnexpected) {
      if (import.meta.env.DEV) console.log('[GameProviders] Using offline cache (unexpected API shape)');
      apiCache = offlineUnexpected;
      apiCacheTimestamp = Date.now();
      return offlineUnexpected;
    }
    return {
      success: false,
      message: response.message || 'Failed to fetch game providers',
      statusCode: response.statusCode || 500,
      error: 'No data from API',
      data: { items: [], statusSummary: { total: 0, active: 0, inactive: 0, maintenance: 0 } }
    };
  } catch (error: any) {
    console.error('[GameProviders] API error:', error?.message || error);
    const offline = loadGameProvidersFromOfflineStorage();
    if (offline) {
      if (import.meta.env.DEV) console.log('[GameProviders] Using offline cache after error');
      apiCache = offline;
      apiCacheTimestamp = Date.now();
      return offline;
    }
    return {
      success: false,
      message: error?.message || 'Failed to fetch game providers',
      statusCode: error?.statusCode || 500,
      error: error?.message || 'Network error',
      data: { items: [], statusSummary: { total: 0, active: 0, inactive: 0, maintenance: 0 } }
    };
  }
}

export interface IconPreloadSummary {
  total: number;
  loaded: number;
  failed: number;
}

function preloadSingleIcon(path: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = async () => {
      try {
        if (typeof img.decode === 'function') await img.decode();
      } catch {
        // Ignore decode failures: onload means network cache already has bytes.
      }
      resolve(true);
    };
    img.onerror = () => resolve(false);
    img.src = path;
  });
}

/**
 * Preload provider icons in background for smoother offline/next-load UX.
 * Already-preloaded paths are skipped in this browser session.
 */
export async function preloadProviderIcons(providerCodes: Array<string | undefined | null>): Promise<IconPreloadSummary> {
  if (typeof window === 'undefined') {
    return { total: 0, loaded: 0, failed: 0 };
  }

  const paths = getProviderIconPaths(providerCodes).filter((path) => !preloadedIconPaths.has(path));
  if (paths.length === 0) {
    return { total: 0, loaded: 0, failed: 0 };
  }

  const results = await Promise.all(paths.map((path) => preloadSingleIcon(path)));
  let loaded = 0;
  let failed = 0;

  results.forEach((ok, index) => {
    if (ok) {
      loaded += 1;
      preloadedIconPaths.add(paths[index]);
    } else {
      failed += 1;
    }
  });

  return { total: paths.length, loaded, failed };
}
